import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { LoadingButton } from '@mui/lab';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import { Notification } from '@features/enterpriseNotifications';
import NotificationIcon from '@features/enterpriseNotifications/components/NotificationIcon/NotificationIcon';
import {
  formNotificationSecondaryText,
  formNotificationTitle,
} from '@features/enterpriseNotifications/components/NotificationListItem/utils';
import { useCreateApplicationNote } from '@features/enterpriseNotifications/hooks/useReadNotification';
import { notificationType } from '@features/notifications/NotificationsEnum';

type NotificationListItemProps = {
  notification: Notification;
  setErrorText: Dispatch<SetStateAction<string>>;
  handleClose: () => void;
  setNotificationsData: Dispatch<SetStateAction<Notification[]>>;
};

const NotificationListItem: FC<NotificationListItemProps> = ({
  notification,
  setErrorText,
  handleClose,
  setNotificationsData,
}) => {
  const navigate = useNavigate();

  const { readNotification, isReadNotificationLoading } =
    useCreateApplicationNote({ setErrorText });

  const handleHireflixNotificationClick = (link: string) => {
    return window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleView = () => {
    setNotificationsData((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)),
    );
    readNotification({
      notification_id: `${notification.id}`,
      user_id: notification.user_id,
    });
    handleClose();

    switch (notification.notification_type) {
      case notificationType.NEW_MESSAGE:
        return navigate(
          '/enterprise/inbox' + `?user=${notification.associated_user?.id}`,
        );
      case notificationType.APPLICANT_COMPLETED_ONBOARDING:
      case notificationType.ONBOARDING_DOCUMENT_EXPIRED:
        return navigate('/enterprise', {
          state: {
            application: JSON.stringify(notification.application!),
            tab: 'onboarding',
          },
        });
      case notificationType.EIN:
        return location.assign('/enterprise/account#Enterprise-Profile');
      case notificationType.PENDING_INTERVIEW_INVITE:
      case notificationType.NO_CALENDAR_BLOCK:
        return navigate('/enterprise/calendar/');
      case notificationType.HIREFLIX_INTERVIEW_COMPLETED:
        return (
          notification?.application?.hire_flix_interview &&
          handleHireflixNotificationClick(
            notification?.application?.hire_flix_interview
              ?.interview_response_url || '',
          )
        );
      case notificationType.AUTO_ACTION_PERFORMED:
        return navigate('/enterprise', {
          state: {
            application: JSON.stringify(notification.application!),
          },
        });
      case 'Company MOVED TO SELF BILLING':
        return navigate('/enterprise/billing/#Manage-Billing');
    }
  };
  return (
    <ListItem
      sx={{ py: 1, flexDirection: { xs: 'column', sm: 'row' } }}
      style={{ justifyContent: 'space-between' }}
    >
      <Stack
        spacing={1}
        direction="row"
        justifyContent="center"
        flexGrow={1}
        sx={{ maxWidth: '410px' }}
      >
        <ListItemIcon sx={{ minWidth: '30px' }} style={{ marginTop: '3px' }}>
          <NotificationIcon notification={notification} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              component="div"
              dangerouslySetInnerHTML={{
                __html: formNotificationTitle(notification),
              }}
              lineHeight={1.2}
            />
          }
          secondary={formNotificationSecondaryText(notification)}
          style={{ color: 'black' }}
        />
      </Stack>
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'center', sm: 'end' }}
        alignItems="center"
        minWidth="250px"
        flexGrow={1}
        pt={{ xs: 2 }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<RemoveRedEyeOutlinedIcon />}
          onClick={handleView}
        >
          View
        </Button>
        <LoadingButton
          variant="outlined"
          size="small"
          startIcon={<CheckOutlinedIcon />}
          loading={isReadNotificationLoading}
          loadingPosition="start"
          onClick={() => {
            setNotificationsData((prev) =>
              prev.map((n) =>
                n.id === notification.id ? { ...n, is_read: true } : n,
              ),
            );
            readNotification({
              notification_id: `${notification.id}`,
              user_id: notification.user_id,
            });
          }}
          style={{ borderRadius: '6px', fontSize: '14px' }}
        >
          Got it
        </LoadingButton>
      </Stack>
    </ListItem>
  );
};

export default NotificationListItem;
